import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import queryClient from 'react/services/queryClient';

/**
 * Helper function to create a promise that resolves after ms milliseconds
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise} Promise that resolves after ms milliseconds
 */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Custom hook that provides actions for stops
 *
 * @param {Object} stop - The stop object
 * @param {number} routeId - The route ID
 * @returns {Object} - Various stop action functions
 */
export const useStopActions = (stop, routeId) => {
  const dispatch = useDispatch();

  const navigateToStopDetails = useCallback(() => {
    const path = `/maps/route/${routeId}/details/stop/${stop.id}`;
    dispatch(push(path));

    Monet.Session.RouteMonitor = Monet.Session.RouteMonitor || {};
    Monet.Session.RouteMonitor.ShortStopDetailsModal = {
      stopId: stop.id
    };
  }, [dispatch, routeId, stop.id]);

  const arriveAtStop = useCallback(() => {
    const arriveStop = new Greenmile.Stop.Arrive({
      parentSelector: '#RouteMonitor',
      successCallback: async () => {
        await sleep(1500);
        queryClient.invalidateQueries(['route-monitor-stops', routeId]);
      }
    });

    arriveStop.setStop(
      new App.Models.StopView({ ...stop, stop: { key: stop.key } })
    );
    arriveStop.setRouteId(routeId);
    arriveStop.show();
  }, [stop, routeId]);

  const cancelStop = useCallback(() => {
    const cancelStopAction = new Greenmile.Stop.Cancel({
      parentSelector: '#RouteMonitor',
      successCallback: async () => {
        await sleep(1500);
        queryClient.invalidateQueries(['route-monitor-stops', routeId]);
      }
    });

    cancelStopAction.setStop(stop.key);
    cancelStopAction.setRouteId(routeId);
    cancelStopAction.show();
  }, [stop.key, routeId]);

  const departFromStop = useCallback(() => {
    const departStop = new Greenmile.Stop.Depart({
      parentSelector: '#RouteMonitor',
      successCallback: async () => {
        await sleep(1500);
        queryClient.invalidateQueries(['route-monitor-stops', routeId]);
      }
    });

    departStop.setStop(
      new App.Models.StopView({
        route: stop.route,
        stop
      })
    );
    departStop.setRouteId(routeId);
    departStop.show();
  }, [stop, routeId]);

  const markAsUndeliverable = useCallback(() => {
    const redeliveryStop = new Greenmile.Stop.Return({
      parentSelector: '#RouteMonitor',
      successCallback: async () => {
        await sleep(1500);
        queryClient.invalidateQueries(['route-monitor-stops', routeId]);
      }
    });

    redeliveryStop.setStop(
      new App.Models.StopView({
        route: stop.route,
        stop
      })
    );
    redeliveryStop.setRouteId(routeId);
    redeliveryStop.show();
  }, [stop, routeId]);

  const markForRedelivery = useCallback(() => {
    const redeliveryStop = new Greenmile.Stop.Undeliverable({
      parentSelector: '#RouteMonitor',
      successCallback: async () => {
        await sleep(1500);
        queryClient.invalidateQueries(['route-monitor-stops', routeId]);
      }
    });

    redeliveryStop.setStop(
      new App.Models.StopView({
        route: stop.route,
        stop
      })
    );
    redeliveryStop.setRouteId(routeId);
    redeliveryStop.show();
  }, [stop, routeId]);

  return {
    navigateToStopDetails,
    arriveAtStop,
    cancelStop,
    departFromStop,
    markAsUndeliverable,
    markForRedelivery
  };
};
